import { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useLocation } from 'react-router'
import config from '@/config'
import { breakpoints, colors, fontWeights } from '@/styles'
import Skeleton from '@/components/skeleton'
import { DisplayTable } from '@/features/Application/elements/tables'
import {
  LabeledValue,
  LabelText,
  ValueText,
  Wrapper,
  EmptyComponent
} from '@/features/Application/elements'
import { isNullOrEmpty } from '@/utils'
import { formatSalaryRange } from '@/utils/format-salary'
import useRole from '@/hooks/use-role'
import useFinancialFiles from '@/hooks/use-financial-files'
import FormatProfession from '@/components/format-profession'
const { APP_PATH } = config

const tabletBreakpoint = '1400px'

const SubtitleWrapper = styled.div`
  grid-column: 1 / 3;

  padding-bottom: 16px;
  border-bottom: 1px solid ${colors.grey88};

  @media only screen and (max-width: ${tabletBreakpoint}) {
    grid-column: unset;
  }
  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    padding-bottom: 0;
  }

  > :first-child {
    grid-template-columns: calc(25% - 12px) 1fr;
    @media only screen and (max-width: ${tabletBreakpoint}) {
      width: 100%;
      grid-template-columns: 1fr 1fr;
      padding: 0;
    }
  }
`

const DocumentsLink = styled.a`
  color: ${colors.link};
  margin: 0;
  font-weight: ${fontWeights.medium};
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;
`

const CustomDataContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  grid-gap: 16px 0;
  ${props =>
    props.showBorder &&
    `

    padding-bottom: 20px;
    border-bottom: 1px solid ${colors.grey88};
  `}
  @media only screen and (max-width: ${tabletBreakpoint}) {
    grid-template-columns: 1fr;
  }
  @media only screen and (max-width: ${breakpoints.desktop}) {
    grid-template-columns: 1fr;
    grid-gap: 0;
    > * {
      padding: 16px 0;
    }
    > :not(:last-child) {
      border-bottom: 1px solid ${colors.grey88};
    }
    @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
      grid-gap: 0;
      > * {
        padding: 12px;
      }
    }
  }
`

const StudentInfoContainer = styled(CustomDataContainer)`
  margin-top: 16px;
  border-top: 1px solid ${colors.grey88};
  padding-top: 16px;

  @media only screen and (max-width: ${breakpoints.desktop}) {
    padding-top: 0px;
    margin-top: 0px;
  }
`

const HalfWidthLabledValue = styled(LabeledValue)`
  width: 50%;
  @media only screen and (max-width: ${tabletBreakpoint}) {
    width: 100%;
    grid-column: unset;
  }
`

const View = ({ application, applicationLoading }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const isStudent = application?.user_info?.job_title === 'student'
  const documentsUrl = `${location?.pathname?.substring(
    0,
    location?.pathname?.lastIndexOf('/')
  )}/documents`

  const handleDocumentsClick = useCallback(
    () => window.open(`${APP_PATH}${documentsUrl}`, '_blank'),
    [documentsUrl]
  )

  const userRole = useRole()
  const secureFilesLength = useFinancialFiles(
    application?.user_info?.files?.user_secure_files,
    userRole
  ).length

  const title = t('b.payment_settings.edit.financial_information.text', 'Financial Information')

  const data = useMemo(
    () => [
      {
        id: 'Legal Name',
        cell: (
          <SubtitleWrapper key="Legal Name">
            <LabeledValue
              label={
                <LabelText>{t('l.application.view.legal_name.label', 'Legal Name')}</LabelText>
              }
              value={
                applicationLoading ? (
                  <Skeleton width={'100%'} height={20} />
                ) : (
                  <ValueText>{`${application?.user_info?.first_name} ${application?.user_info?.last_name}`}</ValueText>
                )
              }
            />
          </SubtitleWrapper>
        )
      },
      {
        id: 'Job Title',
        cell: (
          <LabeledValue
            key="Job Title"
            label={<LabelText>{t('b.profile.general.job_title.text', 'Job Title')}</LabelText>}
            value={
              applicationLoading ? (
                <Skeleton width={'90%'} height={20} />
              ) : (
                <ValueText>
                  {!isNullOrEmpty(application?.user_info?.job_title) ? (
                    <FormatProfession profession={application?.user_info?.job_title} />
                  ) : (
                    '-'
                  )}
                </ValueText>
              )
            }
          />
        )
      },
      {
        id: 'Company Name',
        cell: (
          <LabeledValue
            key="Company Name"
            label={
              <LabelText>
                {isStudent
                  ? t('l.application.view.school_name.label', 'School Name')
                  : t('l.application.view.company_name.label', 'Company Name')}
              </LabelText>
            }
            value={
              applicationLoading ? (
                <Skeleton width={'90%'} height={20} />
              ) : (
                <ValueText>
                  {isStudent
                    ? application?.user_info?.school_name || '-'
                    : application?.user_info?.company_info?.name || '-'}
                </ValueText>
              )
            }
          />
        )
      },
      {
        id: isStudent ? 'Field of Study' : 'Verified Annual Income',
        cell: (
          <LabeledValue
            key={isStudent ? 'Field of Study' : 'Verified Annual Income'}
            label={
              <LabelText>
                {isStudent
                  ? t('l.application.view.field_of_study.label', 'Field of Study')
                  : t('l.application.view.verified_annual_income.label', 'Verified Annual Income')}
              </LabelText>
            }
            value={
              applicationLoading ? (
                <Skeleton width={'90%'} height={20} />
              ) : (
                <ValueText>
                  {isStudent
                    ? application?.user_info?.school_subject
                    : formatSalaryRange(
                        application?.users?.[application?.user_id]?.salary_range,
                        t
                      )}
                </ValueText>
              )
            }
          />
        )
      },
      {
        id: isStudent ? 'Year Started' : 'Verified Documents',
        cell: (
          <LabeledValue
            key={isStudent ? 'Year Started' : 'Verified Documents'}
            label={
              <LabelText>
                {isStudent
                  ? t('l.application.view.year_started.label', 'Year Started')
                  : t('l.application.view.verified_documnents.label', 'Verified Documents')}
              </LabelText>
            }
            value={
              applicationLoading ? (
                <Skeleton width={'90%'} height={20} />
              ) : isStudent ? (
                <ValueText>{application?.user_info?.school_start_date || '-'}</ValueText>
              ) : (
                <DocumentsLink
                  onClick={handleDocumentsClick}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {`${t('l.application.view_documents_link.link', 'View')} (${secureFilesLength})`}
                </DocumentsLink>
              )
            }
          />
        )
      }
    ],
    [applicationLoading]
  )
  console.log('application', application)
  return (
    <Wrapper id="financial-information">
      <DisplayTable
        CustomDataContainer={CustomDataContainer}
        data={data}
        title={title}
        columns={2}
        subColumns={2}
        emptyComponent={
          <EmptyComponent>
            {t(
              'l.application.view.no_employment.label',
              'No employment references included in this application.'
            )}
          </EmptyComponent>
        }
      />
      {isStudent && (
        <StudentInfoContainer>
          <HalfWidthLabledValue
            key="Part Time Work"
            label={
              <LabelText>
                {t('l.application.view.part_time_work.label', 'Part Time Work')}
              </LabelText>
            }
            value={
              <ValueText>
                {application?.user_info?.school_side_working
                  ? t('common.yes', 'Yes')
                  : t('common.no', 'No')}
              </ValueText>
            }
            gridColumns="1 / 3"
          />

          <LabeledValue
            key="Verified Annual Income Student"
            label={
              <LabelText>
                {t('l.application.view.verified_annual_income.label', 'Verified Annual Income')}
              </LabelText>
            }
            value={
              <ValueText>
                {formatSalaryRange(application?.users?.[application?.user_id]?.salary_range, t)}
              </ValueText>
            }
          />
          <LabeledValue
            key="Verified Documents Student"
            label={
              <LabelText>
                {t('l.application.view.verified_documnents.label', 'Verified Documents')}
              </LabelText>
            }
            value={
              <DocumentsLink onClick={handleDocumentsClick}>{`${t(
                'l.application.view_documents_link.link',
                'View'
              )} (${secureFilesLength})`}</DocumentsLink>
            }
          />
        </StudentInfoContainer>
      )}
    </Wrapper>
  )
}

export default View
